import React, { useEffect } from 'react';
import { ArrowLeft, Layers, Grid, Box, GitBranch, Users, CheckCircle, Zap, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TableOfContents from '../components/TableOfContents';

const DesignSystemFoundationsCaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'overview',      label: 'Overview' },
    { id: 'challenge',     label: 'The Challenge' },
    { id: 'approach',      label: 'Approach' },
    { id: 'architecture',  label: 'System Architecture' },
    { id: 'stack',         label: 'The Stack' },
    { id: 'team',          label: 'Team & Process' },
    { id: 'decisions',     label: 'Key Decisions' },
    { id: 'impact',        label: 'Impact' },
  ];

  const systemLayers = [
    {
      layer: 'Layer 1',
      title: 'Design Tokens',
      tag: 'Foundation',
      icon: <Box className="w-5 h-5 text-accent" />,
      desc: 'Color, typography, spacing, border radius, elevation — the raw values everything else inherits from. Single source of truth synced with engineering.',
    },
    {
      layer: 'Layer 2',
      title: 'Component Library',
      tag: 'Building Blocks',
      icon: <Grid className="w-5 h-5 text-accent" />,
      desc: 'Atomic to molecular components in Figma — buttons, inputs, badges, cards, modals — with documented variants, states, and usage guidance.',
    },
    {
      layer: 'Layer 3',
      title: 'AI Interaction Patterns',
      tag: 'AI-Specific',
      icon: <GitBranch className="w-5 h-5 text-accent" />,
      desc: 'Patterns for showing model confidence, making suggestions correctable, progressive disclosure of uncertainty, and graceful handoff when the model is unsure.',
    },
    {
      layer: 'Layer 4',
      title: 'Page Templates & Flow Patterns',
      tag: 'Compositional',
      icon: <Layers className="w-5 h-5 text-accent" />,
      desc: 'Canonical page structures used across products — onboarding, dashboards, detail views, empty states, and error states.',
    },
    {
      layer: 'Layer 5',
      title: 'Research Knowledge Base',
      tag: 'Org Memory',
      icon: <BookOpen className="w-5 h-5 text-accent" />,
      desc: 'A central repository of user research, usability findings, and validated design decisions — built so insights were reusable across products and joiners could onboard faster.',
    },
  ];

  const stack = [
    { tool: 'Figma',     use: 'Design System + Prototyping', desc: 'Primary design and component library tool. Source of truth for the system, with auto-layout and variables wired to tokens.' },
    { tool: 'Mixpanel',  use: 'Analytics + Measurement',     desc: 'Quantitative analytics — funnel analysis, usage tracking, and measuring design decisions against real user behavior.' },
    { tool: 'Claude',    use: 'Research + Ideation',         desc: 'AI-assisted research synthesis, UX copy drafting, and rapid exploration of interaction patterns during early-stage work.' },
    { tool: 'Cursor',    use: 'Code Prototyping',            desc: 'Enabled functional prototypes for high-fidelity testing of AI interactions that Figma could not replicate accurately.' },
    { tool: 'Hotjar',    use: 'User Behavior + Feedback',    desc: 'Heatmaps, session recordings, and on-page feedback to capture qualitative behavioral signals at scale.' },
  ];

  const decisions = [
    {
      decision: 'Separate B2B and B2C system layers',
      rationale: 'Enterprise and consumer products share tokens but diverge at the component level. One system with a clear fork prevented two parallel libraries from drifting silently.',
    },
    {
      decision: 'Ship lean, evolve in production',
      rationale: 'A minimal v1 used across real products teaches faster than a theoretically complete system no one has tested. We iterated based on what products actually demanded.',
    },
    {
      decision: 'Codify AI patterns explicitly',
      rationale: 'Standard design systems do not cover how to show model confidence or make AI output correctable. These were novel patterns that would diverge across products without documentation.',
    },
    {
      decision: 'Research knowledge base over tribal knowledge',
      rationale: 'Insights trapped in individual designer heads do not scale. A shared base meant new joiners could contribute faster and we could build on prior findings instead of repeating research.',
    },
  ];

  return (
    <div className="bg-pastel-bg min-h-screen font-sans text-neutral-900 selection:bg-accent/20 relative">
      <TableOfContents sections={sections} />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-pastel-bg/90 backdrop-blur-md">
        <div className="container mx-auto px-6 md:px-12 h-20 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-neutral-600 hover:text-accent transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="h-px bg-neutral-200/60 mx-6 md:mx-12" />
      </nav>

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* 1. Header */}
          <header id="overview" className="mb-20 scroll-mt-32">
            <h1 className="text-4xl md:text-6xl font-serif text-neutral-900 mb-6 leading-tight">
              Building Enterprise Design System Foundations
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed max-w-3xl mb-10">
              Building the design infrastructure from zero for a division shipping 4 AI products simultaneously — across B2B enterprise and B2C consumer, on web and mobile.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Scope</h3>
                <p className="text-lg font-bold">4 AI Products</p>
                <p className="text-sm text-neutral-500">B2B + B2C, web + mobile</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">Team Built</h3>
                <p className="text-lg font-bold">2 Designers</p>
                <p className="text-sm text-neutral-500">Hired, onboarded, managed</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-neutral-100">
                <h3 className="text-xs uppercase tracking-wider font-bold text-accent mb-2">My Role</h3>
                <p className="text-lg font-bold">Founding Designer</p>
                <p className="text-sm text-neutral-500">Zero → production system</p>
              </div>
            </div>
          </header>

          {/* 2. Challenge */}
          <section id="challenge" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">The Challenge</h2>
            <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
              I joined as the <strong className="text-neutral-900">founding designer of the Analytical AI division</strong>. We were shipping 4 AI products simultaneously — LLM-powered interfaces and ML-driven workflows — across enterprise B2B and consumer B2C, on web and mobile.
            </p>
            <p className="text-lg text-neutral-600 mb-12 leading-relaxed">
              There was no design infrastructure: no shared components, no tokens, no documented patterns, no research process, no prototyping standards. Every product was built in isolation. As the team grew, this created compounding inconsistency and expensive rework.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-accent" /> Consistency Risk
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• 4 products using different UI patterns</li>
                  <li>• No shared tokens or spacing scale</li>
                  <li>• Every handoff rebuilt from scratch</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-accent" /> Velocity Risk
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• Designers rebuilding the same components</li>
                  <li>• No prototyping standards slowed iteration</li>
                  <li>• Research insights not accessible team-wide</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-accent" /> Scale Risk
                </h3>
                <ul className="text-sm text-neutral-600 space-y-2">
                  <li>• No shared language with engineering</li>
                  <li>• New joiners would take months to ramp</li>
                  <li>• AI interaction patterns undocumented</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Approach */}
          <section id="approach" className="mb-24 bg-accent/5 p-10 rounded-[2rem] border border-accent/10 scroll-mt-32">
            <h2 className="text-xl font-bold text-accent mb-4">Approach</h2>
            <p className="text-2xl font-serif text-neutral-900 leading-snug mb-6">
              Build foundations that make doing the right thing the easiest thing — consistency through structure, not through policing.
            </p>
            <p className="text-neutral-600">
              The strategy was to ship a minimal, usable system early and evolve it based on real product needs — not build an exhaustive system upfront that no one tests in the real world.
            </p>
          </section>

          {/* 4. Architecture */}
          <section id="architecture" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-10">System Architecture</h2>
            <div className="space-y-4">
              {systemLayers.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl border border-neutral-100 flex items-start gap-5"
                >
                  <div className="bg-pastel-cream w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs text-neutral-400 font-medium">{item.layer}</span>
                      <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">{item.tag}</span>
                    </div>
                    <h3 className="font-bold text-neutral-900 mb-1">{item.title}</h3>
                    <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* 5. Stack */}
          <section id="stack" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-4">The Stack</h2>
            <p className="text-lg text-neutral-600 mb-10 leading-relaxed">
              I set up the full prototyping and research stack for the team — each tool chosen for a specific role in the design workflow.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {stack.map((item, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-neutral-100">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-neutral-900 text-lg">{item.tool}</h3>
                    <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full mt-1 shrink-0 ml-2">{item.use}</span>
                  </div>
                  <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 6. Team */}
          <section id="team" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Team & Process</h2>
            <div className="bg-white p-8 rounded-3xl border border-neutral-100">
              <ul className="space-y-5">
                {[
                  'Ran hiring end-to-end — wrote job descriptions, screened portfolios, ran design exercises, and made offers',
                  'Managed 2 designers directly: set goals, ran performance reviews, and owned their growth paths',
                  'Established design critiques, async review workflows, and cross-functional sync rhythms with Product and Engineering',
                  'Partnered with Engineering to align on token naming conventions, handoff specs, and component contracts',
                  'Managed external branding vendors — maintained brand integrity while decoupling brand decisions from product work',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-lg text-neutral-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 7. Decisions */}
          <section id="decisions" className="mb-24 scroll-mt-32">
            <h2 className="text-3xl font-serif mb-8">Key Decisions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {decisions.map((item, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-neutral-100">
                  <h3 className="font-bold text-neutral-900 mb-3">{item.decision}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">{item.rationale}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 8. Impact */}
          <section id="impact" className="scroll-mt-32">
            <div className="bg-neutral-900 text-white p-10 rounded-[2.5rem]">
              <h2 className="text-3xl font-serif mb-8">Impact</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-10">
                <div>
                  <h3 className="text-4xl font-bold text-white mb-2">4</h3>
                  <p className="text-neutral-400">AI products on one system</p>
                </div>
                <div>
                  <h3 className="text-4xl font-bold text-white mb-2">2</h3>
                  <p className="text-neutral-400">Designers hired & onboarded</p>
                </div>
                <div>
                  <h3 className="text-4xl font-bold text-white mb-2">0 → 1</h3>
                  <p className="text-neutral-400">Design function built from scratch</p>
                </div>
              </div>
              <p className="text-neutral-300 text-lg leading-relaxed max-w-2xl">
                The system gave 4 products a shared language, made design reviews faster, and let new designers contribute independently within weeks of joining.
              </p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default DesignSystemFoundationsCaseStudy;
